import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Copy, MessageCircle, Smartphone, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "—");
export const setupLink = (code) => `${window.location.origin}/pdks/kurulum#k=${encodeURIComponent(code)}`;

const waPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("90")) return d;
  if (d.startsWith("0")) return `9${d}`;
  return d.length === 10 ? `90${d}` : d;
};

function DeviceInfo({ device }) {
  return (
    <div className="border border-slate-800 rounded-sm p-3 text-xs space-y-1 bg-slate-950" data-testid="pdks-act-device-info">
      <div><span className="text-slate-500">Kurulum:</span> {device.platform === "apk" ? "Android uygulaması (APK)" : device.platform === "pwa" ? "ACUTO PDKS uygulaması" : "Tarayıcı"}</div>
      {device.device_label && <div className="text-slate-400 truncate" title={device.device_label}>{device.device_label}</div>}
      <div><span className="text-slate-500">Aktivasyon:</span> {fmt(device.created_at)}</div>
      <div><span className="text-slate-500">Son kullanım:</span> {fmt(device.last_used_at)}</div>
    </div>
  );
}

function CreatedView({ res, employee }) {
  const link = setupLink(res.code);
  const msg = `Merhaba ${res.employee_name}, ACUTO PDKS kurulum bağlantınız (48 saat geçerli, yalnız size özel):\n${link}`;
  const phone = waPhone(employee.phone);
  return (
    <div className="space-y-3" data-testid="pdks-act-created">
      <div className="flex items-start gap-2 text-emerald-300 text-sm"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />{res.employee_name} için aktivasyon bağlantısı oluşturuldu.</div>
      <Input readOnly value={link} className="h-9 text-[11px] font-mono bg-slate-950 border-slate-800" data-testid="pdks-act-link" onFocus={(e) => e.target.select()} />
      <div className="text-[11px] text-slate-500">Son geçerlilik: {fmt(res.expires_at)} · Tek kullanımlık · Personel yalnız bu linkle kendi telefonunu bağlar.</div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="pdks-act-copy"
          onClick={() => navigator.clipboard?.writeText(link).then(() => toast.success("Bağlantı kopyalandı"), () => toast.error("Kopyalanamadı"))}>
          <Copy className="w-3.5 h-3.5 mr-1.5" />Linki Kopyala
        </Button>
        <Button size="sm" asChild className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="pdks-act-whatsapp">
          <a href={`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-3.5 h-3.5 mr-1.5" />WhatsApp ile Gönder
          </a>
        </Button>
      </div>
    </div>
  );
}

// "PDKS Aktive Et" — tek aktif cihaz kuralı: mevcut cihaz/link yönetici onayı olmadan iptal edilmez
export function PdksActivationDialog({ employee, onClose, onChanged }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const request = useCallback(async (flags = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pdks/mobile/activation/${employee.id}`, flags);
      setRes(data);
      if (data.status === "created") onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); onClose(); }
    finally { setBusy(false); }
  }, [employee, onChanged, onClose]);

  useEffect(() => { setRes(null); if (employee) request(); }, [employee]); // eslint-disable-line

  const replaceDevice = () => {
    if (window.confirm(`${res.employee_name} personelinin mevcut PDKS cihazı iptal edilecek ve yeni aktivasyon bağlantısı oluşturulacak. Onaylıyor musunuz?`)) request({ replace_device: true });
  };

  return (
    <Dialog open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md" data-testid="pdks-act-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-50"><Smartphone className="w-4 h-4 text-blue-400" />PDKS Aktive Et · {employee?.name}</DialogTitle>
        </DialogHeader>
        {busy || !res ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
          : res.status === "created" ? <CreatedView res={res} employee={employee} />
          : res.status === "active_device" ? (
            <div className="space-y-3" data-testid="pdks-act-active-device">
              <div className="flex items-start gap-2 text-amber-300 text-sm"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Bu personelin aktif PDKS cihazı bulunmaktadır.</div>
              <DeviceInfo device={res.device} />
              <div className="flex flex-wrap gap-2 justify-end">
                <Button size="sm" variant="outline" className="rounded-sm border-slate-700" onClick={onClose} data-testid="pdks-act-keep-device">Mevcut Cihaz Kalsın</Button>
                <Button size="sm" className="bg-rose-600 hover:bg-rose-500 rounded-sm" onClick={replaceDevice} data-testid="pdks-act-replace-device">Cihazı İptal Et ve Yeni Aktivasyon Oluştur</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="pdks-act-pending">
              <div className="flex items-start gap-2 text-amber-300 text-sm"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Bu personel için henüz kullanılmamış bir aktivasyon bağlantısı var (son geçerlilik: {fmt(res.expires_at)}).</div>
              <div className="text-xs text-slate-400">Yeni bağlantı oluşturulursa önceki bağlantı geçersiz olur.</div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" className="rounded-sm border-slate-700" onClick={onClose} data-testid="pdks-act-cancel">Vazgeç</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-sm" onClick={() => request({ replace_pending: true })} data-testid="pdks-act-replace-pending">Eski Linki İptal Et, Yenisini Oluştur</Button>
              </div>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
